// Bocetta v4.1 · «color en movimiento». Comportamiento de las secciones bocetta-v4-*.
// Cada sección es un elemento propio que arranca solo al aparecer (también cuando el editor de temas la vuelve a pintar).
// El 3D es el mismo visor de siempre (@bocetta/libreta3d.js), cargado solo cuando su caja está por entrar a la pantalla.
// v4.1 (fluidez): las tarjetas solo animan su libreta al pasar el mouse, los fondos de color se detienen fuera de la vista
// y el hero deja que la propia libreta marque el cambio de color (no se cruza con el pase de hojas).
// v4.2 (todo el sitio): el color de la página vive en <html> (lo elige snippets/bocetta-v4-global.liquid antes del primer pintado),
// el menú «Notebooks» abre el panel de las cuatro libretas, la ficha sigue el color de la variante elegida,
// la franja «lo que trae» se arrastra con el mouse y las tarjetas de la colección agregan al carrito.
// v4.3: las páginas interiores tienen cabecera de color en movimiento (snippets/bocetta-v4-cabecera.liquid).
// v4.4: el azul es el color principal (el ciclo del hero ya no cambia el color del resto del sitio y las cabeceras
// interiores se quedan en Blu), las tarjetas «Our colors» llevan su fondo de color y la libreta abierta,
// la pluma de la portada es su modelo 3D, el fondo hueso tiene destellos de color que se mueven
// y las secciones aparecen suavemente al bajar.
import { ORDEN, quieto, cargar3d, alVerse, sin3d, agregarAlCarrito } from '@bocetta/base.js';

// Tonos del fondo vivo por color: base oscura + tres manchas (el tono real de la libreta y dos vecinos)
export const PALETA = {
  nero: { base: '#0A0B0C', b1: '#2E3134', b2: '#565B61', b3: '#17191B', hex: '#2A292D' },
  blu: { base: '#040B26', b1: '#2F40A0', b2: '#4A6FE0', b3: '#0B1B5C', hex: '#2F40A0' },
  rosso: { base: '#1A0408', b1: '#952B42', b2: '#E0475F', b3: '#4D0A18', hex: '#952B42' },
  viola: { base: '#0F0519', b1: '#56297A', b2: '#9A5BD0', b3: '#2A0C40', hex: '#56297A' }
};

export function pintarColor(el, k) {
  const p = PALETA[k];
  if (!el || !p) return;
  el.style.setProperty('--bc4-base', p.base);
  el.style.setProperty('--bc4-b1', p.b1);
  el.style.setProperty('--bc4-b2', p.b2);
  el.style.setProperty('--bc4-b3', p.b3);
  el.dataset.bc4Color = k;
}

const definir = (nombre, clase) => { if (!customElements.get(nombre)) customElements.define(nombre, clase); };
const editor = () => !!window.Shopify?.designMode;

/* ---------- Fondos de color: se mueven solo mientras se ven ---------- */
let vigia = null;
function vigilarFondos(raiz) {
  if (!('IntersectionObserver' in window)) return;
  vigia ??= new IntersectionObserver((es) => es.forEach((e) => e.target.classList.toggle('es-quieta', !e.isIntersecting)), { rootMargin: '80px' });
  raiz.querySelectorAll('.bc4-mesh').forEach((m) => { m.classList.add('es-quieta'); vigia.observe(m); });
}

class SeccionV4 extends HTMLElement {
  connectedCallback() {
    if (this.listo) return;
    this.listo = true;
    this.ac = new AbortController();
    vigilarFondos(this);
    this.iniciar();
  }
  disconnectedCallback() {
    this.ac?.abort();
    clearInterval(this.reloj);
    this.listo = false;
  }
  iniciar() {}
}

/* ---------- Color de la página: en <html>, para el pie, la ficha y todo lo que no trae color propio ---------- */
let colorPortada = null;
function pintarRaiz(k) {
  const p = PALETA[k];
  if (!p) return;
  const h = document.documentElement;
  pintarColor(h, k);
  h.style.setProperty('--bc4-c', p.hex);
}
const avisarColor = (k) => { colorPortada = k; pintarRaiz(k); document.dispatchEvent(new CustomEvent('bocetta-v4-color', { detail: k })); };
const guardarColor = (k) => { try { localStorage.setItem('bocetta-tu-color', k); } catch {} };

/* ---------- 1 · Hero: la libreta abierta pasa sola por los cuatro colores, o se queda en el que elijas ---------- */

definir('bocetta-v4-hero', class extends SeccionV4 {
  iniciar() {
    const d = this.dataset, sen = { signal: this.ac.signal };
    const botones = [...this.querySelectorAll('[data-color]')];
    const lienzo = this.querySelector('[data-lienzo]');
    const ciclo = d.ciclo === 'true' && !quieto() && !editor();
    let actual = PALETA[d.inicial] ? d.inicial : 'blu';
    let visor = null;

    // v4.7: arranca en azul y, cuando el hero pasa solo por los colores, toda la página lo sigue (fondo, destellos, pie)
    const poner = (k, alSitio = true) => {
      if (!PALETA[k]) return;
      actual = k;
      pintarColor(this, k);
      botones.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.color === k)));
      if (alSitio) avisarColor(k);
    };

    botones.forEach((b) => b.addEventListener('click', () => {
      clearInterval(this.reloj);
      this.reloj = null;
      visor?.detenerCiclo?.();
      poner(b.dataset.color, true);
      visor?.color(b.dataset.color);
    }, sen));

    poner(actual, true);
    // Sin 3D (o mientras carga) el fondo cambia con un reloj; con 3D manda la libreta, que espera a terminar de pasar la hoja
    if (ciclo) {
      this.reloj = setInterval(() => {
        if (document.hidden || visor) return;
        poner(ORDEN[(ORDEN.indexOf(actual) + 1) % ORDEN.length]);
      }, 7000);
    }

    if (lienzo && d.tresD !== 'false' && !sin3d()) {
      lienzo.addEventListener('libreta3d-color', (e) => { if (e.detail !== actual) poner(e.detail); }, sen);
      alVerse(lienzo, () => cargar3d().then(({ libreta3d }) => {
        if (!this.isConnected) return;
        visor = libreta3d(lienzo, actual, {
          quieto: quieto(),
          abierta: d.abierta !== 'false',
          cerca: Number(d.cerca) || 1.05,
          pliegos: 3,
          ciclo: ciclo ? ORDEN : null
        });
      }).catch(() => {}), '100px');
    }

    // Barra de colores: aparece cuando el hero sale de la vista y se esconde al llegar al boletín o al pie
    const barra = document.querySelector('[data-bc4-barra]');
    if (barra && 'IntersectionObserver' in window) {
      let heroVisible = true;
      const finales = new Set();
      const io = new IntersectionObserver((es) => {
        es.forEach((e) => {
          if (e.target === this) heroVisible = e.isIntersecting;
          else if (e.isIntersecting) finales.add(e.target);
          else finales.delete(e.target);
        });
        barra.classList.toggle('es-visible', !heroVisible && finales.size === 0);
      });
      io.observe(this);
      document.querySelectorAll('.bc4-boletin, footer').forEach((el) => io.observe(el));
      this.ac.signal.addEventListener('abort', () => io.disconnect());
      const marcarMini = (k) => barra.querySelectorAll('[data-mini]').forEach((m) => m.classList.toggle('es-actual', m.dataset.mini === k));
      document.addEventListener('bocetta-v4-color', (e) => marcarMini(e.detail), sen);
      marcarMini(actual);
    }
  }
});

/* ---------- Cabeceras de color de las páginas interiores (y de la colección): pasan solas por los colores;
   al elegir uno, toda la página lo toma y se recuerda ---------- */
definir('bocetta-v4-cabecera', class extends SeccionV4 {
  iniciar() {
    const botones = [...this.querySelectorAll('[data-color]')];
    let actual = PALETA[document.documentElement.dataset.bc4Color] ? document.documentElement.dataset.bc4Color : 'blu';
    const poner = (k) => {
      actual = k;
      pintarColor(this, k);
      botones.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.color === k)));
    };
    // v4.6: sin selector; pasan solas por los cuatro colores como el hero.
    // v4.7: y toda la página cambia con ellas, como en la portada. Las de una colección de color (data-ciclo="false") se quedan en su color.
    poner(actual);
    if (this.dataset.ciclo === 'true' && !quieto() && !editor()) {
      this.reloj = setInterval(() => {
        if (document.hidden || this.classList.contains('es-fuera')) return;
        poner(ORDEN[(ORDEN.indexOf(actual) + 1) % ORDEN.length]);
        avisarColor(actual);
      }, 7000);
    }
    botones.forEach((b) => b.addEventListener('click', () => {
      poner(b.dataset.color);
      avisarColor(b.dataset.color);
    }, { signal: this.ac.signal }));
  }
});

/* ---------- Secciones que siguen el color de la portada (boletín) ---------- */
definir('bocetta-v4-sigue', class extends SeccionV4 {
  iniciar() {
    if (colorPortada) pintarColor(this, colorPortada);
    document.addEventListener('bocetta-v4-color', (e) => pintarColor(this, e.detail), { signal: this.ac.signal });
  }
});

/* ---------- 3 · Tarjetas de color: la libreta abierta sobre su color, quieta en reposo; al pasar el mouse gira y pasa hojas ---------- */
definir('bocetta-v4-colores', class extends SeccionV4 {
  iniciar() {
    if (this.dataset.tresD === 'false' || sin3d()) return;
    const carril = this.querySelector('[data-carril]');
    const tactil = matchMedia('(hover: none)').matches;
    alVerse(carril || this, () => cargar3d().then(({ libreta3d }) => {
      if (!this.isConnected) return;
      const tarjetas = [...this.querySelectorAll('[data-tarjeta]')];
      // Una libreta a la vez, con un respiro entre cada una: las texturas se preparan sin congelar el scroll
      tarjetas.forEach((t, i) => setTimeout(() => {
        if (!this.isConnected) return;
        const lienzo = t.querySelector('[data-lienzo]');
        if (!lienzo) return;
        const visor = libreta3d(lienzo, t.dataset.tarjeta, { quieto: quieto(), abierta: true, pliegos: 3, cerca: Number(this.dataset.cerca) || 1 });
        if (!visor.pausa) return; // visor anterior a v4.1
        visor.pausa(true);
        const sen = { signal: this.ac.signal };
        let espera = null;
        const activar = () => { clearTimeout(espera); visor.pausa(false); };
        const reposar = () => { clearTimeout(espera); espera = setTimeout(() => visor.pausa(true), 2400); };
        if (tactil) {
          // En pantallas táctiles se abre al tocar la libreta (el resto de la tarjeta sigue siendo el enlace)
          lienzo.addEventListener('pointerdown', () => { activar(); clearTimeout(espera); espera = setTimeout(reposar, 3500); }, sen);
          return;
        }
        t.addEventListener('pointerenter', activar, sen);
        t.addEventListener('pointerleave', reposar, sen);
        t.addEventListener('focusin', activar, sen);
        t.addEventListener('focusout', (e) => { if (!t.contains(e.relatedTarget)) reposar(); }, sen);
      }, i * 700));
    }).catch(() => {}));
  }
});

/* ---------- 5 · Ciencia del color: al abrir un dato, la sección y la libreta toman su color ---------- */
definir('bocetta-v4-ciencia', class extends SeccionV4 {
  iniciar() {
    const datos = [...this.querySelectorAll('details[data-dato]')];
    const lienzo = this.querySelector('[data-lienzo]');
    let visor = null;
    let actual = (datos.find((x) => x.open) || datos[0])?.dataset.dato || 'blu';

    const poner = (k) => {
      if (!PALETA[k]) return;
      actual = k;
      pintarColor(this, k);
      const i = datos.findIndex((x) => x.dataset.dato === k);
      this.style.setProperty('--bc4-avance', `${((i + 1) / Math.max(1, datos.length)) * 100}%`);
      if (visor) { if (visor.tono) visor.tono(k); else visor.color(k); }
    };
    datos.forEach((x) => x.addEventListener('toggle', () => {
      if (x.open && x.dataset.dato !== actual) {
        // Uno abierto a la vez (por si el navegador no entiende <details name>)
        datos.forEach((o) => { if (o !== x && o.open) o.open = false; });
        poner(x.dataset.dato);
      }
    }, { signal: this.ac.signal }));
    poner(actual);

    if (lienzo && this.dataset.tresD !== 'false' && !sin3d()) {
      alVerse(lienzo, () => cargar3d().then(({ libreta3d }) => {
        if (!this.isConnected) return;
        visor = libreta3d(lienzo, actual, { quieto: quieto(), abierta: false, cerca: Number(this.dataset.cerca) || 1.05, pliegos: 1 });
      }).catch(() => {}));
    }
  }
});

/* ---------- 7 · Pluma de repuesto: agrega al carrito sin salir de la página ---------- */
definir('bocetta-v4-pluma', class extends SeccionV4 {
  iniciar() {
    // v4.4: la foto es el modelo 3D de la pluma; el visor (model-viewer) se carga cuando la caja está por verse
    const modelo = this.querySelector('model-viewer');
    if (modelo) alVerse(modelo, () => cargarModelViewer(), '200px');
    const form = this.querySelector('form[data-agregar]');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      const id = form.querySelector('[name="id"]')?.value;
      if (!id) return;
      e.preventDefault();
      agregarAlCarrito([{ id, quantity: 1 }], form.querySelector('[type="submit"]'));
    }, { signal: this.ac.signal });
  }
});

/* ---------- Visor de modelos 3D de Shopify (model-viewer), para la pluma ---------- */
let visorModelos = null;
function cargarModelViewer() {
  if (customElements.get('model-viewer')) return Promise.resolve();
  visorModelos ??= import('https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js').catch(() => {});
  return visorModelos;
}

/* ---------- Lo que trae cada Bocetta: la franja se arrastra con el mouse; un círculo «Drag» sigue al cursor ---------- */
definir('bocetta-v4-dentro', class extends SeccionV4 {
  iniciar() {
    const carril = this.querySelector('[data-carril]');
    const cursor = this.querySelector('.bc4-dentro__cursor');
    if (!carril) return;
    const sen = { signal: this.ac.signal };
    let x0 = 0, s0 = 0, arrastrando = false, movio = false;
    carril.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      arrastrando = true; movio = false; x0 = e.clientX; s0 = carril.scrollLeft;
      carril.setPointerCapture(e.pointerId);
      this.classList.add('es-arrastrando');
    }, sen);
    carril.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (cursor) cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      if (!arrastrando) return;
      const dx = e.clientX - x0;
      if (Math.abs(dx) > 4) movio = true;
      carril.scrollLeft = s0 - dx;
    }, sen);
    const soltar = () => { arrastrando = false; this.classList.remove('es-arrastrando'); };
    carril.addEventListener('pointerup', soltar, sen);
    carril.addEventListener('pointercancel', soltar, sen);
    carril.addEventListener('click', (e) => { if (movio) { e.preventDefault(); e.stopPropagation(); movio = false; } }, { ...sen, capture: true });
    carril.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.classList.add('con-cursor'); }, sen);
    carril.addEventListener('pointerleave', () => { this.classList.remove('con-cursor'); soltar(); }, sen);
  }
});

/* ---------- Colección: el «+» de cada tarjeta agrega ese color al carrito ---------- */
definir('bocetta-v4-coleccion', class extends SeccionV4 {
  iniciar() {
    this.addEventListener('click', (e) => {
      const b = e.target.closest('[data-agregar]');
      if (!b) return;
      agregarAlCarrito([{ id: b.dataset.agregar, quantity: 1 }], b);
    }, { signal: this.ac.signal });
  }
});

/* ---------- Menú: al pasar por «Notebooks» se abre el panel con las cuatro libretas (como el de Pouch) ---------- */
function iniciarMenu() {
  const plantilla = document.getElementById('bc4-menu-plantilla');
  const cabecera = document.querySelector('#header-component');
  if (!plantilla || !cabecera || editor()) return;
  const base = plantilla.content.firstElementChild;
  if (!base) return;
  const ruta = (u) => { try { return new URL(u, location.href).pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, ''); } catch { return ''; } };
  const destino = ruta(base.dataset.disparador);
  const enlaces = [...cabecera.querySelectorAll('.header-menu .menu-list__link')];
  let disparadores = enlaces.filter((a) => { const r = ruta(a.href); return r === destino || /^\/collections\/(colored-paper-notebooks|all)\/?$/.test(r); });
  if (!disparadores.length && enlaces[0]) disparadores = [enlaces[0]];
  if (!disparadores.length) return;

  const grande = matchMedia('(hover: hover) and (min-width: 990px)');
  let menu = null, reloj = null;
  const crear = () => {
    if (menu) return menu;
    menu = base.cloneNode(true);
    (cabecera.closest('#header-group') || document.body).after(menu);
    menu.addEventListener('pointerenter', () => clearTimeout(reloj));
    menu.addEventListener('pointerleave', cerrarLuego);
    menu.addEventListener('focusout', (e) => { if (!menu.contains(e.relatedTarget) && !disparadores.includes(e.relatedTarget)) cerrar(); });
    menu.querySelector('[data-bc4-menu-cerrar]')?.addEventListener('click', () => { cerrar(); disparadores[0].focus(); });
    return menu;
  };
  const abrir = () => {
    if (!grande.matches) return;
    clearTimeout(reloj);
    const m = crear();
    m.style.setProperty('--bc4-menu-top', `${Math.max(0, Math.round(cabecera.getBoundingClientRect().bottom))}px`);
    m.hidden = false;
    requestAnimationFrame(() => m.classList.add('es-abierto'));
    disparadores.forEach((a) => a.setAttribute('aria-expanded', 'true'));
  };
  function cerrar() {
    if (!menu || menu.hidden) return;
    clearTimeout(reloj);
    menu.classList.remove('es-abierto');
    disparadores.forEach((a) => a.setAttribute('aria-expanded', 'false'));
    reloj = setTimeout(() => { if (!menu.classList.contains('es-abierto')) menu.hidden = true; }, 260);
  }
  function cerrarLuego() { clearTimeout(reloj); reloj = setTimeout(cerrar, 180); }

  disparadores.forEach((a) => {
    a.setAttribute('aria-haspopup', 'true');
    a.setAttribute('aria-expanded', 'false');
    a.addEventListener('pointerenter', abrir);
    a.addEventListener('pointerleave', cerrarLuego);
    a.addEventListener('focus', abrir);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') cerrar(); });
  addEventListener('scroll', () => { if (menu && !menu.hidden && !menu.matches(':hover')) cerrar(); }, { passive: true });
}

/* ---------- Ficha: al elegir otro color cambian el fondo de la galería, las migas y el pie ---------- */
function seguirVariante() {
  if (document.documentElement.dataset.bc4Plantilla !== 'product') return;
  document.addEventListener('change', (e) => {
    const i = e.target;
    if (!(i instanceof HTMLInputElement) || !i.closest('variant-picker')) return;
    document.querySelectorAll('[data-bc4-migas-variante]').forEach((el) => { el.textContent = i.value; });
    const k = String(i.value || '').trim().toLowerCase();
    if (PALETA[k]) { avisarColor(k); guardarColor(k); }
  });
}

/* ---------- Ficha: el modelo 3D de la galería es la libreta nueva (no el archivo 3D viejo subido al producto) ----------
   Shopify guarda un modelo 3D por color como foto del producto; aquí ese lugar de la galería lo ocupa el visor
   de @bocetta/libreta3d.js abierto y pasando hojas, del color elegido. Solo en la libreta (la pluma conserva el suyo). */
function libretaEnGaleria() {
  const h = document.documentElement;
  if (h.dataset.bc4Plantilla !== 'product' || !h.hasAttribute('data-bc4-fijo') || sin3d()) return;
  const zona = document.querySelector('.product-information__media');
  if (!zona) return;
  const poner = () => {
    zona.querySelectorAll('product-model').forEach((pm) => {
      const slide = pm.closest('slideshow-slide, .product-media-container') || pm.parentElement;
      if (!slide || slide.querySelector('.bc4-galeria3d')) return;
      const k = PALETA[h.dataset.bc4Color] ? h.dataset.bc4Color : 'nero';
      const fig = document.createElement('figure');
      fig.className = 'bc4-galeria3d';
      slide.classList.add('bc4-con-3d');
      slide.appendChild(fig);
      alVerse(fig, () => cargar3d().then(({ libreta3d }) => {
        // v4.7: en pantallas angostas la libreta se acerca más (se veía muy chica)
        if (fig.isConnected) libreta3d(fig, k, { abierta: true, pliegos: 3, quieto: quieto(), cerca: matchMedia('(max-width: 749px)').matches ? 1 : matchMedia('(max-width: 1099px)').matches ? 1.12 : 0.82 });
      }).catch(() => {}), '50px');
    });
    // Fotos sin fondo: la de «cuatro vistas» tiene su versión transparente en Archivos (…-four-views-sin-fondo.png)
    zona.querySelectorAll('img').forEach((img) => {
      for (const at of ['src', 'srcset']) {
        const val = img.getAttribute(at);
        if (val && val.includes('-four-views.png')) img.setAttribute(at, val.replaceAll('-four-views.png', '-four-views-sin-fondo.png'));
      }
    });
    // La miniatura del modelo viejo se cambia por la etiqueta «3D»
    zona.querySelectorAll('img').forEach((img) => {
      if (!/preview_images\//.test(img.currentSrc || img.src || '')) return;
      const caja = img.closest('button, a, li');
      if (caja && !caja.closest('.bc4-con-3d')) caja.classList.add('bc4-miniatura-3d');
    });
  };
  poner();
  let espera = null;
  new MutationObserver(() => { clearTimeout(espera); espera = setTimeout(poner, 80); }).observe(zona, { childList: true, subtree: true });
}

/* ---------- Ficha de la pluma: su galería es solo el modelo 3D, que se carga solo (sin tocar el botón) ---------- */
function plumaEnGaleria() {
  const h = document.documentElement;
  if (h.dataset.bc4Plantilla !== 'product' || h.hasAttribute('data-bc4-fijo')) return;
  const modelos = document.querySelectorAll('.product-information__media product-model');
  if (!modelos.length) return;
  // El visor 3D de Shopify se enfoca solo al cargar (y otra vez al armar sus botones): sin mover la página
  const enfocar = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (o) { return enfocar.call(this, this.tagName === 'MODEL-VIEWER' ? { ...(o || {}), preventScroll: true } : o); };
  modelos.forEach((pm) => {
    alVerse(pm, () => {
      const boton = pm.querySelector('.deferred-media__poster-button');
      if (pm.querySelector('model-viewer') || !boton) return;
      // El visor se enfoca al cargar y la página saltaba hacia abajo: durante unos segundos, si el visitante
      // no ha tocado nada, la página se queda donde estaba
      const pw = document.querySelector('.page-wrapper');
      const caja = pw && pw.scrollHeight > pw.clientHeight + 4 ? pw : document.scrollingElement;
      const fuente = caja === document.scrollingElement ? window : caja, y = caja.scrollTop;
      let tocado = false;
      const marcar = () => { tocado = true; };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((ev) => addEventListener(ev, marcar, { once: true, passive: true, capture: true }));
      const fijar = () => { if (!tocado && Math.abs(caja.scrollTop - y) > 4) caja.scrollTop = y; };
      fuente.addEventListener('scroll', fijar, { passive: true });
      setTimeout(() => fuente.removeEventListener('scroll', fijar), 4000);
      boton.click();
    }, '50px');
  });
}

/* ---------- Fondo vivo: destellos de color que se mueven despacio detrás de las secciones claras ---------- */
function fondoVivo() {
  if (document.querySelector('.bc4-fondo-vivo')) return;
  const f = document.createElement('div');
  f.className = 'bc4-fondo-vivo';
  f.setAttribute('aria-hidden', 'true');
  // Chispas en lugares fijos (no al azar: la página se ve igual en cada visita)
  const chispas = [
    [8, 14, 0, '#4A6FE0', 1], [91, 9, 1.6, '#2F40A0', 0.8], [76, 31, 3.1, '#9A5BD0', 0.7], [14, 46, 2.2, '#4A6FE0', 0.9],
    [52, 22, 4.4, '#E0475F', 0.6], [95, 58, 0.8, '#4A6FE0', 1.1], [4, 78, 3.7, '#2F40A0', 0.7], [63, 69, 1.2, '#9A5BD0', 0.8],
    [33, 88, 2.9, '#4A6FE0', 1], [84, 86, 4.9, '#E0475F', 0.6], [42, 52, 5.6, '#2F40A0', 0.5], [24, 6, 6.3, '#9A5BD0', 0.6]
  ].map(([x, y, d, c, s]) => `<i class="bc4-chispa" style="--x:${x}%;--y:${y}%;--d:${d}s;--c:${c};--s:${s}"></i>`).join('');
  f.innerHTML = `<span class="bc4-brillo bc4-brillo--a"></span><span class="bc4-brillo bc4-brillo--b"></span><span class="bc4-brillo bc4-brillo--c"></span>${chispas}`;
  document.body.prepend(f);
}

/* ---------- Aparecer al bajar: títulos, tarjetas y secciones suben y se encienden al entrar a la pantalla ---------- */
function aparecerAlBajar() {
  if (quieto() || editor() || !('IntersectionObserver' in window)) return;
  const grupos = [
    '.bc4-cabeza', '.bc4-carril > .bc4-tarjeta', '.bc4-ciencia__lado', '.bc4-ciencia__libreta', '.bc4-quiz__cuerpo > *',
    '.bc4-pluma__foto', '.bc4-pluma__texto', '.bc4-comunidad :is(figure, li)', '.bc4-coleccion__rejilla > li',
    '.bc4-porque__pie',
    'main > .shopify-section:not(:first-child):not(:has(.bc4, .bocetta-quiz-bloque, product-information, .product-information))'
  ];
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    if (!e.isIntersecting) return;
    e.target.classList.add('bc4-visto');
    io.unobserve(e.target);
  }), { rootMargin: '0px 0px -8% 0px' });
  const vistos = new Set();
  grupos.forEach((sel) => {
    let lista = [];
    try { lista = [...document.querySelectorAll(sel)]; } catch { return; }
    lista.forEach((el) => {
      if (vistos.has(el) || el.closest('.bc4-hero, .bc4-cabecera, .bc4-menu, header, .bc4-aparece')) return;
      vistos.add(el);
      const hermanos = el.parentElement ? [...el.parentElement.children] : [];
      el.style.setProperty('--bc4-retraso', `${(hermanos.indexOf(el) % 4) * 90}ms`);
      el.classList.add('bc4-aparece');
      io.observe(el);
    });
  });
}

/* ---------- Páginas vacías (carrito vacío, 404, búsqueda sin resultados): los cuatro colores para seguir ---------- */
function sugerirColores() {
  const h = document.documentElement, t = h.dataset.bc4Plantilla;
  const main = document.querySelector('main');
  if (!main || document.querySelector('.bc4-sugerencias')) return;
  const vacio = t === '404'
    || (t === 'cart' && !document.querySelector('main .cart-items__table-row'))
    || (t === 'search' && !document.querySelector('main product-card'));
  if (!vacio) return;
  const es = (h.lang || '').startsWith('es');
  const pre = es ? '/es' : '';
  const datos = [
    ['nero', 'Nero', es ? 'enfoque' : 'focus', 'nero-black-paper-notebook'],
    ['blu', 'Blu', es ? 'calma' : 'calm', 'blu-blue-paper-notebook'],
    ['rosso', 'Rosso', es ? 'energía' : 'energy', 'rosso-red-paper-notebook'],
    ['viola', 'Viola', es ? 'introspección' : 'introspection', 'viola-purple-paper-notebook']
  ];
  const s = document.createElement('section');
  s.className = 'bc4 bc4-sugerencias';
  s.innerHTML = `<div class="bc4-wrap"><p class="bc4-mono bc4-etiqueta">${es ? 'Mientras tanto · los cuatro colores' : 'Meanwhile · the four colors'}</p>
    <ul class="bc4-sugerencias__lista" role="list">${datos.map(([k, n, a, hd]) => `<li><a class="bc4-color bc4-sugerencia" data-bc4-color="${k}" href="${pre}/collections/${hd}">
      <span class="bc4-mesh" aria-hidden="true"><span class="bc4-mancha bc4-mancha--a"></span><span class="bc4-mancha bc4-mancha--b"></span><span class="bc4-mancha bc4-mancha--c"></span></span>
      <span class="bc4-serif bc4-sugerencia__nombre">${n}</span><span class="bc4-mono bc4-sugerencia__animo">${es ? 'Para quien busca' : 'For those who seek'} ${a}</span><span class="bc4-sugerencia__flecha" aria-hidden="true">↗</span></a></li>`).join('')}</ul></div>`;
  const primera = main.querySelector(':scope > .shopify-section');
  if (primera) primera.after(s); else main.appendChild(s);
  s.querySelectorAll('.bc4-sugerencia').forEach((a) => pintarColor(a, a.dataset.bc4Color));
}

/* ---------- Modo ligero en vivo: si la página va a tirones (cuadros lentos), se apagan los efectos caros ---------- */
function vigilarFluidez() {
  const h = document.documentElement;
  if (h.classList.contains('bc4-ligero') || editor()) return;
  let n = 0, prev = 0, lentos = 0, ventanas = 0, seguidas = 0;
  const paso = (t) => {
    if (prev) { if (t - prev > 34) lentos++; n++; }
    prev = t;
    if (n >= 120) {
      ventanas++;
      if (lentos / n > 0.5) { seguidas++; if (seguidas >= 2) { h.classList.add('bc4-ligero'); return; } } else seguidas = 0;
      if (ventanas >= 5) return; // ~10 s de muestra después de cargar
      n = 0; lentos = 0;
    }
    requestAnimationFrame(paso);
  };
  // Empieza cuando la página ya cargó (la carga misma siempre tiene tirones)
  const empezar = () => setTimeout(() => requestAnimationFrame(paso), 1500);
  if (document.readyState === 'complete') empezar(); else addEventListener('load', empezar, { once: true });
}

/* ---------- Páginas sin 3D: en tiempo libre se baja el visor y se prepara la libreta azul (queda guardada en el navegador),
   así la portada o la ficha abren su 3D al instante ---------- */
function precalentar3d() {
  const h = document.documentElement;
  if (!h.classList.contains('bc4-3d') || h.classList.contains('bc4-ligero') || editor()) return;
  if (document.querySelector('[data-lienzo], [data-lienzo-3d], .product-information__media product-model')) return;
  const c = navigator.connection;
  if (c && (c.saveData || /2g/.test(c.effectiveType || ''))) return;
  const ir = () => (window.requestIdleCallback || setTimeout)(() => cargar3d().then((m) => m.precalentar && m.precalentar(['blu'])).catch(() => {}), { timeout: 6000 });
  if (document.readyState === 'complete') setTimeout(ir, 2500); else addEventListener('load', () => setTimeout(ir, 2500), { once: true });
}

/* ---------- Secciones sin comportamiento propio (quiz, comunidad…): solo detienen su fondo fuera de la vista ---------- */
document.querySelectorAll('.bc4-quiz').forEach(vigilarFondos);

/* ---------- Todo el sitio ---------- */
if (document.documentElement.hasAttribute('data-bc4-sitio') && !window.__bc4Sitio) {
  window.__bc4Sitio = true;
  const k = document.documentElement.dataset.bc4Color;
  if (!document.querySelector('bocetta-v4-hero') && PALETA[k]) avisarColor(k);
  iniciarMenu();
  seguirVariante();
  libretaEnGaleria();
  plumaEnGaleria();
  sugerirColores();
  fondoVivo();
  vigilarFluidez();
  precalentar3d();
  aparecerAlBajar();
  // Quiz: al salir el resultado, la página toma el color que le tocó al visitante
  if (document.querySelector('.bocetta-quiz-bloque')) {
    document.addEventListener('bocetta-color', (e) => { if (PALETA[e.detail]) avisarColor(e.detail); });
  }
}
